import React, { useRef } from 'react';
import { CloseIcon, IconButton } from '@razorpay/blade/components';
import { useModal } from 'components/ui/Modals';
import styles from './index.module.scss';
import useClickOutside from 'hooks/useClickOutside';

type ModalProps = {
  disableClose?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
};

type ModalTitleProps = {
  children: React.ReactNode;
  className?: string;
};

const Modal = ({ disableClose, children, shouldCloseOnOutsideClick = false, onClose }: ModalProps) => {
  const ref = useRef(null);
  const { closeModal } = useModal();

  useClickOutside({
    ref,
    onOutSideClick: () => (disableClose ? null : shouldCloseOnOutsideClick ? closeModal() : null),
  });

  const handleClose = () => {
    onClose && onClose();
    closeModal();
  };

  return (
    <div className={styles.Body} ref={ref}>
      {disableClose ? (
        <></>
      ) : (
        // Dismiss control. A plain Blade ghost IconButton (transparent, no filled
        // background) so it reads as a low-emphasis action that stays below the
        // modal's primary CTA. `emphasis="subtle"` gives a white glyph that is
        // legible on the modal's dark surface; `accessibilityLabel` is "Close"
        // to match Blade's own ModalHeader, and to stay clear of the OTP
        // modal's "Close Modal" button, which e2e locators match by substring.
        // The wrapping div only handles the top-right positioning (see
        // styles.Close).
        <div className={styles.Close}>
          <IconButton
            icon={CloseIcon}
            accessibilityLabel="Close"
            emphasis="subtle"
            onClick={handleClose}
          />
        </div>
      )}

      <div className="modal-text">{children}</div>
    </div>
  );
};

export const ModalTitle = ({ children, className = '' }: ModalTitleProps) => {
  return <h3 className={`text-4xl font-bold ${className}`}>{children}</h3>;
};

export default Modal;
